import {
  normalizeTenantRoles,
  TENANT_ADMIN_ROLE,
  TENANT_MEMBER_ROLE,
  TRANSFORMATION_LEAD_ROLE,
  type TenantRole,
} from "@playbookai/types";
import { csrfHeaders } from "../auth/session-api.js";

/**
 * The tenant role vocabulary is `@playbookai/types`' (ADR-0078), re-exported so
 * the admin pages keep one import. Roles are ADDITIVE — `tenant-admin` and
 * `transformation-lead` are held independently on top of `tenant-member` — so
 * every wire shape here carries a `roles` SET, never a single select value.
 */
export type { TenantRole };
export { TENANT_ADMIN_ROLE, TENANT_MEMBER_ROLE, TRANSFORMATION_LEAD_ROLE };
export type MembershipStatus = "active" | "suspended" | "removed";
export type AuthenticationMethod = "microsoft-entra" | "local";

export interface DirectoryUser {
  directoryUserId: string;
  displayName: string;
  email: string | null;
  userPrincipalName: string;
  department: string | null;
  jobTitle: string | null;
  membership: { userId: string; status: MembershipStatus } | null;
}

export interface AdminMember {
  userId: string;
  displayName: string | null;
  email: string | null;
  department: string | null;
  jobTitle: string | null;
  /** Every tenant role held, in vocabulary order; always holds `tenant-member`. */
  roles: readonly TenantRole[];
  status: MembershipStatus;
  source: "admin" | "deployment_bootstrap";
  firstSignedInAt: string | null;
  lastSignedInAt: string | null;
  syncStatus: string | null;
  authenticationMethod: AuthenticationMethod;
  /**
   * Whether a Transformation Lead removal is RECORDED and not yet complete for
   * this member (ADR-0078). Carried by the members LIST only — computed
   * server-side in one batch read per page — and absent from single-member
   * responses, hence optional. True from the moment the operation exists,
   * including after the role is revoked but before the final pass: exactly
   * when the row's role checkbox is already unchecked and nothing else would
   * say the removal still has to finish. The Members page offers Resume from it.
   */
  transformationLeadRemovalInProgress?: boolean;
  /**
   * PB-5345 review run 8. A reset whose credential landed but whose sign-out
   * never finished, so the member may still be signed in somewhere (AC-6).
   *
   * SERVER-DECIDED AND CARRIED ON THE ROW, because the alternative was a dialog
   * held in browser memory: closing it or reloading lost the only route that
   * closes the recorded stage. Optional for the same reason the marker beside
   * it is — an older server simply omits it.
   */
  passwordResetSignOutPending?: boolean;
  /**
   * PB-5294. An OUTSTANDING set-password link for this member — they have an
   * account and have not chosen a password, so they cannot sign in. Null once
   * they have, and for anyone who predates this flow.
   *
   * Carried by the members LIST only, computed server-side in one batch read
   * per page, hence optional.
   *
   * `mailOutcome` is what the notification service answered for that link's
   * email: `no_sender` (this workspace has no mail server configured, so
   * nothing was attempted), `rejected` (attempted and refused), `sent`, or
   * null while it is still queued. Kept apart rather than collapsed, because
   * each one calls for a different action from the administrator.
   */
  setPassword?: {
    readonly pending: boolean;
    readonly mailOutcome: string | null;
  } | null;
  /**
   * PB-5294. The member joined by redeeming an invitation, rather than being
   * provisioned directly by an administrator with a temporary password.
   *
   * The two look identical on this list — both `source: "admin"`, both local,
   * both never signed in — and only the first can be sent a set-password link:
   * the second already holds a credential, so the server refuses every re-send.
   * Carried by the members LIST only, computed server-side in one batch read per
   * page, hence optional.
   */
  joinedByInvitation?: boolean;
  /**
   * PB-5345. Whether this member still has to choose a password — the server's
   * answer, not something to re-derive here.
   *
   * The three facts above cannot supply it: completing a set-password link
   * retires the token, so `setPassword` goes null, while `joinedByInvitation`
   * stays historical and `firstSignedInAt` stays null until the member signs
   * in. Both credential row actions read this one field, because they are exact
   * opposites and two heuristics for one question is how they came to disagree.
   *
   * Carried by the members LIST only, computed server-side in one batch read per
   * page, hence optional.
   */
  owesPassword?: boolean;
}

export interface AdminMemberPage {
  users: AdminMember[];
  nextCursor: string | null;
}

export interface AdminMemberNumberedPage {
  users: AdminMember[];
  page: number;
  pageSize: number;
  totalCount: number;
  pageCount: number;
}

/** `tenant-admin` held, whatever else is. Additive: says nothing about LAMA. */
export function isTenantAdmin(member: Pick<AdminMember, "roles">): boolean {
  return member.roles.includes(TENANT_ADMIN_ROLE);
}

/** `transformation-lead` held — LAMA management. Independent of admin. */
export function isTransformationLead(
  member: Pick<AdminMember, "roles">,
): boolean {
  return member.roles.includes(TRANSFORMATION_LEAD_ROLE);
}

/**
 * The member's BASE access — the half of the role set the Members page's
 * access select edits. The Transformation Lead half is a separate control,
 * because the two are independent and a single select would make them
 * mutually exclusive (the plan's first listed risk).
 */
export type BaseAccess = typeof TENANT_MEMBER_ROLE | typeof TENANT_ADMIN_ROLE;

export function baseAccessOf(member: Pick<AdminMember, "roles">): BaseAccess {
  return isTenantAdmin(member) ? TENANT_ADMIN_ROLE : TENANT_MEMBER_ROLE;
}

/**
 * The COMPLETE role set to send when one half changes and the other is kept.
 * Canonicalised through `normalizeTenantRoles`, so two admins describing the
 * same set send the same bytes and `tenant-member` is never dropped.
 */
export function composeTenantRoles(
  baseAccess: BaseAccess,
  transformationLead: boolean,
): readonly TenantRole[] {
  return normalizeTenantRoles([
    baseAccess,
    ...(transformationLead ? [TRANSFORMATION_LEAD_ROLE] : []),
  ]);
}

export async function searchDirectoryUsers(
  query: string,
): Promise<DirectoryUser[]> {
  const response = await request(
    `/api/auth/admin/directory-users?query=${encodeURIComponent(query)}&limit=20`,
  );
  return (response as { users: DirectoryUser[] }).users;
}

/** Server-side filters for the members list. All are optional; omitting status
    lists current memberships (`active | suspended`) and excludes soft-removed
    history. `query` matches display name or email; `role` keeps only members
    who HOLD that role (the replacement picker asks for `transformation-lead`). */
export interface AdminMemberFilters {
  readonly query?: string;
  readonly status?: MembershipStatus;
  readonly role?: TenantRole;
  /** Exclude people already in this tenant-scoped directory group. */
  readonly excludeDirectoryGroupId?: string;
}

/**
 * The tenant-scoped PREFIX of every members key. Tenant-scoped so a session swap
 * inside a still-mounted /admin tree cannot serve one tenant's member names and
 * email addresses to the next tenant's administrator (PB-1556), and so an
 * invalidation reaches only the tenant it was raised for.
 *
 * Invalidate with this, not with a fully-specified key: TanStack matches by
 * prefix, so it clears every filter combination the admin has visited, whereas a
 * key carrying the filters would clear only the view on screen.
 */
export function adminMembersQueryKeyPrefix(
  tenantId: string | null | undefined,
): readonly unknown[] {
  return ["auth", "admin", "members", tenantId ?? "none"] as const;
}

/** The key for ONE filtered page set. Filters are part of it so a filter change
    starts a new page set instead of appending a differently-filtered page to the
    previous one. */
export function adminMembersQueryKey(
  tenantId: string | null | undefined,
  filters: AdminMemberFilters = {},
): readonly unknown[] {
  return [
    ...adminMembersQueryKeyPrefix(tenantId),
    filters.query ?? "",
    filters.status ?? "all",
    filters.role ?? "any",
  ] as const;
}

export function adminMembersNumberedQueryKey(
  tenantId: string | null | undefined,
  page: number,
  filters: AdminMemberFilters = {},
): readonly unknown[] {
  return [
    ...adminMembersQueryKey(tenantId, filters),
    "numbered",
    page,
  ] as const;
}

/**
 * ONE member's LAMA ownership inventory (ADR-0078). Tenant-scoped like every
 * other admin key, and keyed per member: the removal dialog reads it for the
 * person it is about to remove, and a handover must not show one tenant's
 * counts under another tenant's member.
 */
export function adminLamaOwnershipQueryKey(
  tenantId: string | null | undefined,
  userId: string,
): readonly unknown[] {
  return [
    "auth",
    "admin",
    "lama-ownership",
    tenantId ?? "none",
    userId,
  ] as const;
}

/** The tenant-scoped PREFIX of every directory-search key. Same rationale as
    the members prefix, and it binds harder here: the search results carry
    directory display names and emails under a 60s `staleTime`, so an unscoped
    key has a real window in which it serves the previous tenant's people. */
export function adminDirectoryQueryKeyPrefix(
  tenantId: string | null | undefined,
): readonly unknown[] {
  return ["auth", "admin", "directory", tenantId ?? "none"] as const;
}

/** The key for one directory search term. */
export function adminDirectoryQueryKey(
  tenantId: string | null | undefined,
  query: string,
): readonly unknown[] {
  return [...adminDirectoryQueryKeyPrefix(tenantId), query] as const;
}

/** The key for ONE member's sign-in sessions (PB-2927).
 *
 * Tenant-scoped for the same reason as the two above, and it is not theoretical
 * here: `userId` is not unique across tenants, and this PR's own integration
 * spec proves one user holding live sessions in two of them. Without the tenant
 * segment, an in-place tenant handover would serve the previous tenant's device
 * labels and activity times from cache. The server fences every request on the
 * acting tenant regardless, so this is a cache defect, not an authorization
 * hole — but it is still a disclosure. */
export function adminMemberSessionsQueryKey(
  tenantId: string | null | undefined,
  userId: string | null,
): readonly unknown[] {
  return [
    "auth",
    "admin",
    "member-sessions",
    tenantId ?? "none",
    userId,
  ] as const;
}

/* The endpoint's MAX_LIMIT (services/auth/src/admin/admin.controller.ts) — it
   400s above this rather than clamping, so the client must not exceed it.
   Exported because the readiness admin-access scan states how many members it
   looked at, and that sentence is only true while it derives the number from
   the page size actually sent. */
export const MEMBER_PAGE_SIZE = 50;
export const NUMBERED_MEMBER_PAGE_SIZE = 25;

/**
 * `signal` is optional and additive — existing callers are unaffected. It exists
 * for the readiness admin-access scan, which can issue up to ten SEQUENTIAL
 * requests: without it, navigating away mid-scan leaves the remainder in flight.
 * TanStack hands a signal to every `queryFn`, so a caller that has one only has
 * to pass it on.
 */
export async function listAdminMembers(
  cursor?: string | null,
  filters: AdminMemberFilters = {},
  signal?: AbortSignal,
): Promise<AdminMemberPage> {
  const params = new URLSearchParams({ limit: String(MEMBER_PAGE_SIZE) });
  if (cursor) params.set("cursor", cursor);
  /* Filtering is server-side: a tenant can hold far more members than any
     client-side filter over the loaded pages could honestly search. */
  const query = filters.query?.trim();
  if (query) params.set("query", query);
  if (filters.status) params.set("status", filters.status);
  if (filters.role) params.set("role", filters.role);
  if (filters.excludeDirectoryGroupId) {
    params.set("excludeDirectoryGroupId", filters.excludeDirectoryGroupId);
  }
  return (await request(
    `/api/auth/admin/users?${params.toString()}`,
    signal ? { signal } : {},
  )) as AdminMemberPage;
}

/** Numbered mode is used only by the visible roster. Cursor callers such as
    readiness scans and role-removal pickers keep the legacy contract above. */
export async function listAdminMembersPage(
  page: number,
  filters: AdminMemberFilters = {},
  signal?: AbortSignal,
): Promise<AdminMemberNumberedPage> {
  const params = new URLSearchParams({
    page: String(page),
    pageSize: String(NUMBERED_MEMBER_PAGE_SIZE),
  });
  const query = filters.query?.trim();
  if (query) params.set("query", query);
  if (filters.status) params.set("status", filters.status);
  if (filters.role) params.set("role", filters.role);
  if (filters.excludeDirectoryGroupId) {
    params.set("excludeDirectoryGroupId", filters.excludeDirectoryGroupId);
  }
  return (await request(
    `/api/auth/admin/users?${params.toString()}`,
    signal ? { signal } : {},
  )) as AdminMemberNumberedPage;
}

export async function provisionDirectoryUser(
  directoryUserId: string,
  roles: readonly TenantRole[],
): Promise<void> {
  await request("/api/auth/admin/users", {
    method: "POST",
    body: JSON.stringify({
      directoryUserId,
      roles: normalizeTenantRoles(roles),
    }),
  });
}

export interface LocalUserProvisioningResponse {
  member: AdminMember;
  temporaryPassword: string;
}

export async function provisionLocalUser(
  displayName: string,
  email: string,
  roles: readonly TenantRole[],
): Promise<LocalUserProvisioningResponse> {
  return (await request("/api/auth/admin/local-users", {
    method: "POST",
    body: JSON.stringify({
      displayName,
      email,
      roles: normalizeTenantRoles(roles),
    }),
  })) as LocalUserProvisioningResponse;
}

/**
 * Replace the member's COMPLETE additive role set. Removing
 * `transformation-lead` this way is refused server-side (`409`): that goes
 * through `removeTransformationLead`, which transfers what they own first.
 */
export async function updateTenantRoles(
  userId: string,
  roles: readonly TenantRole[],
): Promise<void> {
  await request(
    `/api/auth/admin/users/${encodeURIComponent(userId)}/tenant-role`,
    {
      method: "PUT",
      body: JSON.stringify({ roles: normalizeTenantRoles(roles) }),
    },
  );
}

/**
 * What a member owns and holds in LAMA, as COUNTS (ADR-0078). Read through Auth
 * to Playbook BE, which owns the state; no titles and no ids, so a Tenant Admin
 * who is not a Transformation Lead learns nothing about LAMA content here.
 * Questionnaires are owned or not — ownership is their whole authorization
 * record — so only campaigns carry a non-owner membership count.
 */
export interface LamaOwnershipInventory {
  readonly questionnairesOwned: number;
  readonly campaignsOwned: number;
  readonly campaignMemberships: number;
}

export async function getLamaOwnership(
  userId: string,
  signal?: AbortSignal,
): Promise<LamaOwnershipInventory> {
  return (await request(
    `/api/auth/admin/users/${encodeURIComponent(userId)}/lama-ownership`,
    signal ? { signal } : {},
  )) as LamaOwnershipInventory;
}

export interface RemoveTransformationLeadInput {
  /** An active lead other than the member, or `null` when they own nothing. */
  readonly replacementUserId: string | null;
  /**
   * Client-chosen, unique per removal attempt SERIES: a retry sends the same
   * key and resumes the same operation, so the transfer can never run twice.
   */
  readonly idempotencyKey: string;
}

export interface TransformationLeadRemovalResult {
  readonly operationId: string;
  /** The only status a `200` carries — anything short of it is a `409`/`503`. */
  readonly status: "completed";
  /** Who ACTUALLY received the ownership; `null` when nothing was owned. */
  readonly replacementUserId: string | null;
  readonly cleanup: {
    readonly questionnairesTransferred: number;
    readonly campaignsTransferred: number;
    readonly campaignMembershipsRevoked: number;
  };
  readonly member: AdminMember;
}

/**
 * The staged, idempotent Transformation Lead removal (ADR-0078): transfer the
 * member's LAMA ownership to the replacement, revoke their other campaign
 * memberships, revoke the role, sign them out, then a final transfer pass. A
 * `409 replacement_required` names the counts; `replacement_ineligible`,
 * `removal_in_progress` and `role_not_held` are the other refusals. A `503`
 * means Playbook BE could not be reached — the reached stage holds and the same
 * key retries.
 */
export async function removeTransformationLead(
  userId: string,
  input: RemoveTransformationLeadInput,
): Promise<TransformationLeadRemovalResult> {
  return (await request(
    `/api/auth/admin/users/${encodeURIComponent(userId)}/transformation-lead/remove`,
    {
      method: "POST",
      body: JSON.stringify({
        replacementUserId: input.replacementUserId,
        idempotencyKey: input.idempotencyKey,
      }),
    },
  )) as TransformationLeadRemovalResult;
}

/** The last stage a removal committed; `completed` is never in flight. */
export type TransformationLeadRemovalStage =
  | "pending"
  | "cleanup_completed"
  | "role_revoked";

/**
 * An in-flight removal as the recovery read reports it (ADR-0078): what the
 * dialog needs to RESUME it under its own key after a close, a navigation or
 * a reload — by the administrator who started it or by another. Ids and stage
 * only: no names, no LAMA content.
 */
export interface TransformationLeadRemovalOperation {
  readonly operationId: string;
  readonly idempotencyKey: string;
  readonly status: TransformationLeadRemovalStage;
  readonly replacementUserId: string | null;
  /**
   * Why the attempt at the next stage stopped — `cleanup_unavailable`,
   * `replacement_required`, `replacement_ineligible`,
   * `session_invalidation_failed` — or `null` while an attempt has recorded no
   * outcome yet.
   */
  readonly lastErrorCode: string | null;
  readonly requestedByUserId: string;
  /**
   * An attempt currently holds the operation's claim, so a second one would be
   * refused. The dialog waits rather than offering the action.
   */
  readonly attemptInProgress: boolean;
  /**
   * Whether the next attempt may name a DIFFERENT replacement. False while an
   * attempt is claimed, and false when the last attempt left the transfer's
   * outcome unknown — the recorded replacement may already hold the member's
   * LAMA ownership, and redirecting is what would split it between two people.
   * Derived server-side, so the dialog cannot offer a change the write refuses.
   */
  readonly replacementChangeAllowed: boolean;
  readonly createdAt: string;
  readonly updatedAt: string;
}

/**
 * ONE member's in-flight removal, tenant-scoped like every admin key and read
 * fresh every time (`staleTime: 0` at the call site): the whole point of the
 * read is to learn what another attempt may just have changed.
 */
export function adminLeadRemovalQueryKey(
  tenantId: string | null | undefined,
  userId: string,
): readonly unknown[] {
  return ["auth", "admin", "lead-removal", tenantId ?? "none", userId] as const;
}

export async function getTransformationLeadRemoval(
  userId: string,
  signal?: AbortSignal,
): Promise<TransformationLeadRemovalOperation | null> {
  const body = (await request(
    `/api/auth/admin/users/${encodeURIComponent(userId)}/transformation-lead/removal`,
    signal ? { signal } : {},
  )) as { operation: TransformationLeadRemovalOperation | null };
  return body.operation;
}

export async function updateMembershipStatus(
  userId: string,
  status: MembershipStatus,
): Promise<void> {
  await request(`/api/auth/admin/users/${encodeURIComponent(userId)}/status`, {
    method: "PUT",
    body: JSON.stringify({ status }),
  });
}

/**
 * One live sign-in session of a member (PB-2927).
 *
 * There is no session id here, and none is wanted: the id is the cookie value,
 * so the server withholds it rather than hand an administrator a working
 * credential for someone else. `userAgent` arrives raw and becomes a device
 * label for display — parsing belongs on this side, not in the auth service.
 */
export interface ActiveSession {
  readonly createdAt: number;
  readonly updatedAt: number;
  readonly userAgent: string | null;
}

export async function listMemberSessions(
  userId: string,
  signal?: AbortSignal,
): Promise<readonly ActiveSession[]> {
  return (await request(
    `/api/auth/admin/users/${encodeURIComponent(userId)}/sessions`,
    signal ? { signal } : {},
  )) as readonly ActiveSession[];
}

/**
 * PB-5345. Finish a reset whose sign-out did not complete.
 *
 * NOT `revokeMemberSessions`, though it revokes: that endpoint knows nothing
 * about the reset, so retrying through it left the durable operation open
 * saying the member might still be signed in. This one closes it.
 */
export async function retryPasswordResetSignOut(userId: string): Promise<{
  readonly outcome: "completed" | "still_incomplete" | "nothing_open";
}> {
  return (await request(
    `/api/auth/admin/members/${encodeURIComponent(userId)}/password-reset/sign-out`,
    { method: "POST" },
  )) as { readonly outcome: "completed" | "still_incomplete" | "nothing_open" };
}

export async function revokeMemberSessions(
  userId: string,
): Promise<{ readonly revoked: number }> {
  return (await request(
    `/api/auth/admin/users/${encodeURIComponent(userId)}/sessions/revoke`,
    { method: "POST" },
  )) as { readonly revoked: number };
}

/**
 * PB-5294 (AC-7). Mint a fresh set-password link for a member and enqueue its
 * email. The new link supersedes any outstanding one.
 *
 * RETURNS THE URL, exactly once, and that is the point of the call rather
 * than a convenience. A workspace with no mail server configured cannot
 * deliver this message at all, so without the link in hand an administrator
 * looking at somebody who cannot sign in has no action that helps. The
 * invitation flow returns `inviteUrl` the same way and for the same reason —
 * it was removed once and had to be restored.
 *
 * Show it once, with a copy affordance, and do not persist it.
 */
export async function resendSetPasswordEmail(
  userId: string,
): Promise<{ readonly expiresAt: string; readonly setPasswordUrl: string }> {
  return (await request(
    `/api/auth/admin/members/${encodeURIComponent(userId)}/set-password-email`,
    { method: "POST" },
  )) as { readonly expiresAt: string; readonly setPasswordUrl: string };
}

/**
 * PB-5345. The response carries a password that exists nowhere else.
 *
 * It is present whenever the credential was written, even when a step after
 * that write did not complete — withholding it would add a locked-out member to
 * whatever else went wrong. `signOut` and `recorded` are how the server says
 * what did not finish, and both are decided there: a second copy of either rule
 * in the browser is a second thing to drift.
 *
 * `not_needed` is gone: it existed only for the self-reset case, which the
 * server now refuses outright (AC-6, review run 4), so every accepted reset
 * attempts the sign-out and `incomplete` is the only way it can fall short.
 */
export interface MemberPasswordResetResponse {
  readonly temporaryPassword: string;
  readonly signOut: "revoked" | "incomplete";
  readonly recorded: boolean;
}

export async function resetMemberPassword(
  userId: string,
): Promise<MemberPasswordResetResponse> {
  return (await request(
    `/api/auth/admin/members/${encodeURIComponent(userId)}/password-reset`,
    { method: "POST" },
  )) as MemberPasswordResetResponse;
}

/**
 * PB-540. `services/auth` now returns the documented `{ code, message }` envelope
 * on every failure, so both halves are useful and both are carried: `message` is
 * a human sentence safe to render as-is, and `code` is the stable discriminator
 * UI copy keys on (see `ADMIN_ERROR_COPY` in MembersAdmin).
 *
 * Before the filter existed auth emitted Nest's `{ statusCode, message, error }`
 * for most failures, so this layer threw the *code* as the message just to get a
 * stable string. That inverted precedence would now put a machine code in front
 * of an admin instead of the server's sentence.
 */
export class AdminApiError extends Error {
  readonly code: string | undefined;
  /**
   * The refusal body as the server sent it (ADR-0078). Auth's staged removal
   * puts what a client needs to RECOVER into the body — the in-flight
   * operation's `idempotencyKey`, `stage`, `replacementUserId` — and a code
   * alone cannot carry that. `undefined` when the body was not JSON.
   */
  readonly details: unknown;

  constructor(message: string, code: string | undefined, details?: unknown) {
    super(message);
    this.name = "AdminApiError";
    this.code = code;
    this.details = details;
  }
}

/** A string field off an error body, or `null` when absent or not a string. */
export function adminErrorDetail(error: unknown, field: string): string | null {
  if (!(error instanceof AdminApiError)) return null;
  const details = error.details;
  if (typeof details !== "object" || details === null) return null;
  const value = (details as Record<string, unknown>)[field];
  return typeof value === "string" ? value : null;
}

/**
 * How a service's failure body becomes the message and code an admin reads.
 *
 * The transport is shared; the envelope SHAPE is not. Auth's global filter puts
 * `code` and `message` at the top level (PB-540) and every caller here spoke
 * that dialect — but AIS nests the same fields under `error`
 * (`services/ais/ais/errors.py`), so a flat read yielded `undefined` for both
 * and fell back to the status line for every distinct refusal. Making the
 * reader the CALLER's declaration keeps one fetch path without any client
 * having to know another service's contract (PB-4987).
 */
export type AdminErrorReader = (
  body: unknown,
  status: number,
) => { message: string; code: string | undefined };

/** Auth's flat `{ code, message }` envelope, and the default. `message` wins
 * because it is the sentence the admin reads; `code` is carried for callers
 * that key copy on it, and is preferred over the status line so a body with a
 * code and no sentence still puts a discriminator in front of them. */
export const readFlatErrorEnvelope: AdminErrorReader = (body, status) => {
  const flat = body as { code?: string; message?: string } | null;
  return {
    message: flat?.message ?? flat?.code ?? `Request failed (${status})`,
    code: flat?.code,
  };
};

/** Fetch helper — CSRF + credentials + an error envelope raised as an
 * `AdminApiError`. `readError` defaults to auth's flat envelope, so the callers
 * that speak it are unaffected; a caller talking to a service with a different
 * failure shape passes its own reader. Shared with invites-api;
 * tenant-config-api deliberately keeps its own variant — see the rationale
 * there. */
export async function request(
  path: string,
  init: RequestInit = {},
  readError: AdminErrorReader = readFlatErrorEnvelope,
): Promise<unknown> {
  const response = await fetch(path, {
    ...init,
    credentials: "include",
    headers: {
      Accept: "application/json",
      ...(init.body ? { "Content-Type": "application/json" } : {}),
      ...csrfHeaders(),
      ...init.headers,
    },
  });
  if (!response.ok) {
    const body: unknown = await response.json().catch(() => null);
    const { message, code } = readError(body, response.status);
    throw new AdminApiError(message, code, body ?? undefined);
  }
  return response.status === 204 ? null : response.json();
}
